import { createSelector } from 'reselect';

import { RootState } from 'store/types';

import { WidgetKeys } from '../enums';
import { ValidationType } from '../enums';
import { createSelectValidationByWidget } from '../utils';

import { IrAnalysisState } from './types';

export const selectIrAnalysisState = (state: RootState) =>
  state.irAnalysis as IrAnalysisState;

export const selectGlobalState = (state: RootState) => state.global;

export const selectCurrencies = createSelector(
  selectGlobalState,
  (globalState) => globalState.currencies,
);

export const selectCurrenciesLoading = createSelector(
  selectGlobalState,
  (globalState) => globalState.currenciesLoading,
);

export const selectCompanyEntities = createSelector(
  selectGlobalState,
  (globalState) => globalState.companyEntities,
);

export const selectCompanyEntitiesLoading = createSelector(
  selectGlobalState,
  (globalState) => globalState.companyEntitiesLoading,
);

export const selectIRIndices = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.irIndices.data,
);

export const selectIRIndicesLoading = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.irIndices.loading,
);

export const selectIRIndicesError = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.irIndices.error,
);

export const selectSwapTenors = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.swapTenors.data,
);

export const selectSwapTenorsLoading = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.swapTenors.loading,
);

export const selectSwapTenorsError = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.swapTenors.error,
);

export const selectIRMarketEnvironment = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.irMarketEnvironment,
);

export const selectDebtHedgeSchedule = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.debtHedgeSchedule,
);

export const selectPricingSummary = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.pricingSummary,
);

export const selectCashflowThroughTime = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.cashflowThroughTime,
);

export const selectDownloadRMToolsReportLoading = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.downloadRMToolsReport?.loading ?? false,
);

export const selectDebtTableData = (widgetKey: WidgetKeys) =>
  createSelector(selectIrAnalysisState, (state) => {
    if (widgetKey === WidgetKeys.CASHFLOW_THROUGH_TIME)
      return state.cashflowThroughTime.debtTableData;
    return state.debtHedgeSchedule.debtTableData;
  });

export const selectDebtTableLoading = (widgetKey: WidgetKeys) =>
  createSelector(selectIrAnalysisState, (state) => {
    if (widgetKey === WidgetKeys.CASHFLOW_THROUGH_TIME)
      return state.cashflowThroughTime.debtTableLoading;
    return state.debtHedgeSchedule.debtTableLoading;
  });

export const selectTradeTableData = (widgetKey: WidgetKeys) =>
  createSelector(selectIrAnalysisState, (state) => {
    if (widgetKey === WidgetKeys.CASHFLOW_THROUGH_TIME)
      return state.cashflowThroughTime.tradeTableData;
    return state.debtHedgeSchedule.tradeTableData;
  });

export const selectTradeTableLoading = (widgetKey: WidgetKeys) =>
  createSelector(selectIrAnalysisState, (state) => {
    if (widgetKey === WidgetKeys.CASHFLOW_THROUGH_TIME)
      return state.cashflowThroughTime.tradeTableLoading;
    return state.debtHedgeSchedule.tradeTableLoading;
  });

export const selectClosestValuationDate = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.closestValuationDate?.value,
);

export const selectClosestValuationDateLoading = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.closestValuationDate?.loading,
);

export const selectIrValidations = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.validations,
);

export const selectIrValidationByRunDate = (
  widgetKey: WidgetKeys,
  runDate: string,
) =>
  createSelector(selectIrValidations, (validations) => {
    const key = `${widgetKey}_${runDate}`;
    return validations?.[key] ?? { loading: false, valid: null, error: null };
  });

export const selectValidateTradesByWidget = (widgetKey: WidgetKeys) =>
  createSelectValidationByWidget(ValidationType.TRADES, widgetKey);

export const selectValidateDebtsByWidget = (widgetKey: WidgetKeys) =>
  createSelectValidationByWidget(ValidationType.DEBTS, widgetKey);

export const selectUserWidgetsData = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.userWidgets.data,
);

export const selectUserWidgetsLoading = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.userWidgets.loading,
);

export const selectDisableDebtAndHedge = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.disbaleDebtAndHedge,
);

export const selectDisableCFTT = createSelector(
  selectIrAnalysisState,
  (irAnalysisState) => irAnalysisState.disableCFTT,
);